import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/ui/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ChevronDown, ExternalLink, Loader2, MapPin, Pencil } from "lucide-react";
import { useState } from "react";
import { ActionBar } from "@/lib/components/ActionBar";
import { FeatureCard } from "@/lib/components/FeatureCard";
import { PlaceMiniMap } from "@/lib/components/map/PlaceMiniMap";
import { usePlatsDetail } from "@/lib/hooks/usePlatsDetail";
import { trpc } from "@/router";

// ─── Main view ──────────────────────────────────────────────────────────────
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function PlatsDetailView() {
  const $ = _c(53);
  const p = usePlatsDetail();
  if (p.isLoading) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = /*#__PURE__*/_jsx("div", {
        className: "flex min-h-[60vh] items-center justify-center",
        children: /*#__PURE__*/_jsx(Loader2, {
          className: "h-6 w-6 animate-spin text-gray-400"
        })
      });
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (!p.place) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = /*#__PURE__*/_jsx("p", {
        className: "text-gray-500",
        children: "Platsen hittades inte."
      });
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== p.navigateToKarta) {
      t1 = /*#__PURE__*/_jsxs("div", {
        className: "flex min-h-[60vh] flex-col items-center justify-center gap-2",
        children: [t0, /*#__PURE__*/_jsx(Button, {
          variant: "outline",
          onClick: p.navigateToKarta,
          children: "Tillbaka till kartan"
        })]
      });
      $[2] = p.navigateToKarta;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t0;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = /*#__PURE__*/_jsx(ArrowLeft, {
      className: "h-4 w-4"
    });
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== p.navigateToKarta) {
    t1 = /*#__PURE__*/_jsxs("button", {
      onClick: p.navigateToKarta,
      className: "flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700",
      children: [t0, "Karta"]
    });
    $[5] = p.navigateToKarta;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== p.place.name) {
    t2 = /*#__PURE__*/_jsx("div", {
      className: "min-w-0 flex-1",
      children: /*#__PURE__*/_jsx("p", {
        className: "truncate text-sm font-medium text-gray-900",
        children: p.place.name
      })
    });
    $[7] = p.place.name;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== p.isAdmin || $[10] !== p.place.id) {
    t3 = p.isAdmin && /*#__PURE__*/_jsx(Link, {
      to: "/admin/platser/$id",
      params: {
        id: String(p.place.id)
      },
      className: "text-xs text-gray-400 hover:text-gray-600",
      children: /*#__PURE__*/_jsx(Pencil, {
        className: "h-3.5 w-3.5"
      })
    });
    $[9] = p.isAdmin;
    $[10] = p.place.id;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = /*#__PURE__*/_jsx("div", {
      className: "sticky top-0 z-20 border-b bg-white/95 backdrop-blur-sm",
      children: /*#__PURE__*/_jsxs("div", {
        className: "mx-auto flex h-12 max-w-4xl items-center gap-3 px-4",
        children: [t1, t2, t3]
      })
    });
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== p.featurePins || $[17] !== p.place.location.latitude || $[18] !== p.place.location.longitude) {
    t5 = /*#__PURE__*/_jsx("div", {
      className: "h-56 md:h-72",
      children: /*#__PURE__*/_jsx(PlaceMiniMap, {
        latitude: p.place.location.latitude,
        longitude: p.place.location.longitude,
        features: p.featurePins
      })
    });
    $[16] = p.featurePins;
    $[17] = p.place.location.latitude;
    $[18] = p.place.location.longitude;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== p.place.name) {
    t6 = /*#__PURE__*/_jsx("h1", {
      className: "text-2xl font-bold text-gray-900",
      children: p.place.name
    });
    $[20] = p.place.name;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== p.place.categoryName) {
    t7 = p.place.categoryName && /*#__PURE__*/_jsx("p", {
      className: "mt-1 text-sm text-gray-500",
      children: p.place.categoryName
    });
    $[22] = p.place.categoryName;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== p.place.description) {
    t8 = p.place.description && /*#__PURE__*/_jsx("p", {
      className: "mt-3 text-sm leading-relaxed text-gray-600",
      children: p.place.description
    });
    $[24] = p.place.description;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
    t9 = /*#__PURE__*/_jsxs("div", {
      className: "mb-4",
      children: [t6, t7, t8]
    });
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const t10 = p.place.location;
  let t11;
  if ($[30] !== p.place.id || $[31] !== p.place.name) {
    t11 = {
      placeId: p.place.id,
      placeName: p.place.name
    };
    $[30] = p.place.id;
    $[31] = p.place.name;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const t12 = p.navigateToBidra;
  const t13 = String(p.place.id);
  let t14;
  if ($[33] !== t13) {
    t14 = {
      to: "/admin/platser/$id",
      params: {
        id: t13
      }
    };
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== p.navigateToBidra || $[36] !== p.place.location || $[37] !== t11 || $[38] !== t14) {
    t15 = /*#__PURE__*/_jsx("div", {
      className: "mb-6",
      children: /*#__PURE__*/_jsx(ActionBar, {
        location: t10,
        bidra: t11,
        onBidra: t12,
        editLink: t14
      })
    });
    $[35] = p.navigateToBidra;
    $[36] = p.place.location;
    $[37] = t11;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== p.grouped) {
    t16 = p.grouped.length > 0 ? /*#__PURE__*/_jsx("div", {
      className: "space-y-6",
      children: p.grouped.map(_temp)
    }) : /*#__PURE__*/_jsxs("div", {
      className: "rounded-lg border bg-gray-50 px-4 py-8 text-center",
      children: [/*#__PURE__*/_jsx(MapPin, {
        className: "mx-auto mb-2 h-8 w-8 text-gray-300"
      }), /*#__PURE__*/_jsx("p", {
        className: "text-sm font-medium text-gray-600",
        children: "Ingen tillg\xE4nglighetsinformation \xE4nnu"
      }), /*#__PURE__*/_jsx("p", {
        className: "mt-1 text-xs text-gray-400",
        children: "Hj\xE4lp oss genom att rapportera tillg\xE4ngligheten"
      })]
    });
    $[40] = p.grouped;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== p.place.id) {
    t17 = /*#__PURE__*/_jsx(PlaceExperiences, {
      placeId: p.place.id
    });
    $[42] = p.place.id;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t15 || $[45] !== t16 || $[46] !== t17 || $[47] !== t9) {
    t18 = /*#__PURE__*/_jsxs("div", {
      className: "mx-auto max-w-4xl px-4 py-6",
      children: [t9, t15, t16, t17]
    });
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t9;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t18 || $[50] !== t4 || $[51] !== t5) {
    t19 = /*#__PURE__*/_jsxs("div", {
      className: "min-h-dvh bg-white",
      children: [t4, t5, t18]
    });
    $[49] = t18;
    $[50] = t4;
    $[51] = t5;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  return t19;
}

// ─── Feature group ──────────────────────────────────────────────────────────
function _temp(group) {
  return /*#__PURE__*/_jsx(FeatureGroup, {
    group: group
  }, group.name);
}
function FeatureGroup(t0) {
  const $ = _c(17);
  const {
    group
  } = t0;
  const [expanded, setExpanded] = useState(group.features.length <= 3);
  let t1;
  if ($[0] !== expanded || $[1] !== group.features) {
    t1 = expanded ? group.features : group.features.slice(0, 2);
    $[0] = expanded;
    $[1] = group.features;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shown = t1;
  let t2;
  if ($[3] !== group.features.length || $[4] !== group.name) {
    t2 = /*#__PURE__*/_jsxs("h2", {
      className: "mb-2 text-sm font-semibold uppercase tracking-wider text-gray-400",
      children: [group.name, " (", group.features.length, ")"]
    });
    $[3] = group.features.length;
    $[4] = group.name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== shown) {
    t3 = shown.map(_temp2);
    $[6] = shown;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = /*#__PURE__*/_jsx("div", {
      className: "space-y-3",
      children: t3
    });
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== expanded || $[11] !== group.features.length) {
    t5 = !expanded && group.features.length > 2 && /*#__PURE__*/_jsxs("button", {
      onClick: () => setExpanded(true),
      className: "mt-2 flex items-center gap-1 text-sm text-brand hover:underline",
      children: [/*#__PURE__*/_jsx(ChevronDown, {
        className: "h-4 w-4"
      }), "Visa alla ", group.features.length]
    });
    $[10] = expanded;
    $[11] = group.features.length;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t2 || $[14] !== t4 || $[15] !== t5) {
    t6 = /*#__PURE__*/_jsxs("div", {
      children: [t2, t4, t5]
    });
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}

// ─── Experiences ────────────────────────────────────────────────────────────
function _temp2(feature) {
  return /*#__PURE__*/_jsx(FeatureCard, {
    feature: feature
  }, feature.id);
}
function PlaceExperiences(t0) {
  const $ = _c(7);
  const {
    placeId
  } = t0;
  let t1;
  if ($[0] !== placeId) {
    t1 = trpc.experiences.byPlaceId.queryOptions({
      placeId
    });
    $[0] = placeId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: experiences
  } = useQuery(t1);
  if (!experiences || experiences.length === 0) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = /*#__PURE__*/_jsx("h2", {
      className: "mb-3 text-sm font-semibold uppercase tracking-wider text-gray-400",
      children: "Upplevelser"
    });
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== experiences) {
    t3 = experiences.map(_temp3);
    $[3] = experiences;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = /*#__PURE__*/_jsxs("div", {
      className: "mt-6",
      children: [t2, /*#__PURE__*/_jsx("div", {
        className: "space-y-2",
        children: t3
      })]
    });
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp3(exp) {
  return /*#__PURE__*/_jsxs(Link, {
    to: "/upptack/$slug",
    params: {
      slug: exp.titleSlug ?? exp.id
    },
    className: "flex items-center gap-3 rounded-lg border bg-white p-3 transition-colors hover:bg-gray-50",
    children: [exp.coverUrl ? /*#__PURE__*/_jsx("img", {
      src: exp.coverUrl,
      alt: "",
      className: "h-12 w-12 shrink-0 rounded-lg object-cover"
    }) : /*#__PURE__*/_jsx("div", {
      className: "flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gray-100",
      children: /*#__PURE__*/_jsx(MapPin, {
        className: "h-4 w-4 text-gray-400"
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: "min-w-0",
      children: [/*#__PURE__*/_jsx("p", {
        className: "truncate text-sm font-medium text-gray-900",
        children: exp.title
      }), exp.subtitle && /*#__PURE__*/_jsx("p", {
        className: "truncate text-xs text-gray-500",
        children: exp.subtitle
      })]
    }), /*#__PURE__*/_jsx(ExternalLink, {
      className: "ml-auto h-4 w-4 shrink-0 text-gray-300"
    })]
  }, exp.id);
}